import { Chain } from '@1delta/chain-registry'

/**
 * `PositionLens` — one contract per chain that answers a whole family's user
 * position set in ONE call, skipping the markets the account is not in.
 *
 * WHY THIS EXISTS, in the units that matter: reads nested inside the lens are
 * ordinary EVM calls and cost only gas. What an endpoint rate-limits, caps and
 * stalls on is the REQUEST, and the sharded multicall sizes requests in calls (300
 * max). Measured on Ethereum for an EMPTY account across Silo + LlamaLend + the
 * Compound forks: 1,780 multicall entries over 6 requests and 568 KB of response,
 * against 1 request and 1.9 KB through the lens.
 *
 * Deployed at the SAME address on every chain — CREATE2 through the shared
 * DeployFactory with pinned compiler settings, see `deployPositionLens.ts` in
 * `contracts-delegation`. A chain missing from this map is not broken: every family
 * keeps its per-market read set and falls back to it, which is also the rollout
 * switch and the emergency revert (delete the entry, no redeploy).
 */
export const POSITION_LENS_V1 = '0x6AC2A29f36278772b6C69287e746Fd5Ed217430f'

/**
 * v3 — all fifteen families, live on 20 chains at one address.
 *
 * v2 (`0xbBb1CdDd42AB18B3A43A6377F38e4f422F230Ac6`, Ethereum only) is DEAD and
 * must never be referenced: its Resupply family probed a state-MUTATING
 * `getUserSnapshot` through a `staticcall`, so it returned empty bytes for every
 * account — which is byte-identical to "this account holds nothing".
 */
export const POSITION_LENS_V3 = '0xb05B8adB2675A43513A0198B962F5abAF3bc9edf'

/**
 * v4 — all fifteen families with the probe and accrual fixes. Ethereum only so
 * far; the other 19 chains still run v3.
 *
 * Verified against the live deployment: runtime byte-identical, Fraxlend and
 * Resupply rows carry the pair's own previewing conversion, Morpho rows carry the
 * four accrual inputs matching `market()` exactly, and the Aave header is the
 * bitfield (`0x03` = eModeOk | configOk) with rows and fields matching the walk.
 */
export const POSITION_LENS_V4 = '0x66AfDcb2f3232013d7741e9994D321A47CE1Dd4e'

/**
 * v5 — the live deployment: v4 plus one rule, that an all-zero row with no flag
 * set is not emitted at all.
 *
 * That rule exists to keep the EMITTER and the PARSER saying the same thing. The
 * Compound V2 converter has always dropped an entry with no balances and no
 * collateral flag, and v4's Aave collateral-bit union could produce exactly such a
 * row — 227 bytes that the reader then discards. Aligning them means a row's
 * PRESENCE always carries information.
 *
 * v4 (`0x66AfDcb2…`) is functionally correct and was briefly live on Ethereum and
 * Optimism; both were redeployed here. It is superseded, not broken.
 */
export const POSITION_LENS_V5 = '0xd32eD8255b2A30113E1D1DC1c88F961cC60A2Ff7'

/**
 * v6 — the live deployment on the 22 chains wired before 2026-09-16, at one
 * address. Chains added since (Arc, Tempo, World Chain) went straight to v7.
 *
 * v6 closed four FIELD GAPS: each was the lens emitting fewer words than the
 * converter reads, which no contract-level test could catch — those compare the
 * lens's own fields against the chain and can never see an ABSENT one.
 *
 *  - Aave V4 — `getUserDebt` returns `(drawn, premium)` and the converter SUMS
 *    them; v5 emitted drawn only.
 *  - Midnight — the raw `position` credit/pendingFee are STALE; the contract
 *    accrues only inside `updatePositionView`.
 *  - Fraxlend — the converter divides by `previewAddInterest`'s vault totals.
 *  - Resupply — `getPairAccounting` totals plus the collateral share price.
 *
 * All four verified against live data. See `USER_DATA_READ_PATH.md` §10 for the
 * per-converter field ledger and the procedure to run before wiring any family.
 */
export const POSITION_LENS_V6 = '0x110ff05D2822DBBe1a5404d2a485267e2Ed962C6'

/**
 * v7 — v6 plus the Aave V4 row fix: the last row word is `dynamicConfigKey`
 * (the FIFTH field of `getUserPosition`), which is the only one the converter
 * reads. v6 emitted the first field there, so `AAVE_V4` had to be withheld from
 * it (see `V6_WITHHELD`); v7 declares every family.
 *
 * Deployed 2026-09-16 on Arc (5042), Tempo (4217), World Chain (480) and Ronin (2020) under
 * salt `keccak256("1delta.PositionLens.v7")`, initCodeHash
 * `0x46783b51eb4cbe591ee8f169718add07bb2929f07f77234da6b8a4e32565c7c9`. Runtime
 * verified byte-identical on all four (`eth_getCode` sha256
 * `2b2ec74a…e1e6ae`, 23,211 bytes) with a live `getUserData([]) -> []`. The 22
 * v6 chains have NOT been redeployed: they keep `v6()` and its withhold until
 * each lands here.
 */
export const POSITION_LENS_V7 = '0x388d62698c8E69e9D12cfe1DbDC822AF07d04408'

/** Families the contract knows. Must match the FAMILY_* constants in PositionLens.sol. */
export const LENS_FAMILY = {
  COMPOUND_V2: 1,
  SILO: 2,
  LLAMALEND: 3,
  /**
   * Liquity family (Liquity V2 + every fork). Implemented in `PositionLens.sol`
   * but **not in any deployed contract** — v1 carries families 1-3, and adding a
   * family changes the creation code and therefore the address.
   *
   * Declared here on purpose, and deliberately absent from `V1_FAMILIES`: a
   * caller that wires this family gets `undefined` from `getPositionLensFor` on
   * every chain and takes the per-market probe, which is exactly right until a v2
   * is deployed and the map below is updated per chain.
   */
  LIQUITY: 4,
  /** Aave V2 + V3 (shared `getUserReserveData` shape). Not in any deployed contract. */
  AAVE: 5,
  /** Compound V3 / Comet. Not in any deployed contract. */
  COMET: 6,
  /**
   * Morpho Blue + Lista/Moolah, ported from the standalone `MorphoLens` so both
   * can share ONE address. The packed layout is byte-identical to that contract's
   * `getUserDataCompact` — verified against the live deployment — so the existing
   * decoder keeps working unchanged. Not in any deployed PositionLens yet: until
   * one declares it, Morpho keeps using `MORPHO_LENS`.
   *
   * KNOWN GAP, both contracts: `borrowAssets` is `toAssetsUp` over `market()`
   * totals, which are as-of the market's `lastUpdate` — the pending accrual
   * since then is missing, and on a dormant market that gap is unbounded (a
   * 141-day-stale Base market understated by 0.62 %, which broke a bundler3
   * close sized off it). Fine for display, NEVER for funding — anything sizing
   * a repay/flash must accrue via `IRM.borrowRateView` first (TS reference:
   * worker-api `readMorphoUserState`). The next lens cut should accrue in the
   * contract or emit the market totals + `lastUpdate` so the client can. See
   * USER_DATA_READ_PATH.md §3 "Morpho — the lens reports debt WITHOUT pending
   * accrual".
   */
  MORPHO: 7,
  /** Aave V4, per spoke. Not in any deployed contract. */
  AAVE_V4: 8,
  /** Term Finance. Not in any deployed contract. */
  TERM: 9,
  /** Inverse FiRM. Not in any deployed contract. */
  INVERSE: 10,
  /** Fraxlend. Not in any deployed contract. */
  FRAXLEND: 11,
  /** River (Satoshi). Not in any deployed contract. */
  RIVER: 12,
  /** Morpho Midnight. Not in any deployed contract. */
  MIDNIGHT: 13,
  /**
   * Teller — DISCOVERY FOLDED IN. Its builder costs ~1.7 s across six chains and
   * issues zero batch calls: it is entirely pre-flight. Not deployed yet.
   */
  TELLER: 14,
  /**
   * Resupply — discovery folded in, and the roster is read from the chain: there
   * is no published pair list, `getAllPairAddresses` IS the discovery. Not
   * deployed yet.
   */
  RESUPPLY: 15,
} as const

export type LensFamily = (typeof LENS_FAMILY)[keyof typeof LENS_FAMILY]

/**
 * What a given chain's deployment can actually answer.
 *
 * THE POINT OF THE `families` SET. The address is CREATE2-derived from the
 * creation code, so adding a family produces a DIFFERENT contract at a DIFFERENT
 * address — it can never be an in-place upgrade. That means a period where some
 * chains run v1 and some run v2, and during it a builder that asks v1 for a family
 * it does not know gets empty bytes back. The converter reads that as "the lens
 * declined" and drops the family, so the user's positions simply vanish from that
 * chain: no error, no failed call, nothing to alert on.
 *
 * Declaring the families per deployment removes that failure mode by construction.
 * A family this chain's contract does not implement is not asked — the caller takes
 * the per-market read set instead, which is correct, just slower. Rolling out v2 is
 * then a per-chain map edit as each deploy lands, with no flag day and no window in
 * which data is missing.
 */
export interface LensDeployment {
  address: string
  /** Families this deployment implements. */
  families: ReadonlySet<LensFamily>
}

const V1_FAMILIES: ReadonlySet<LensFamily> = new Set([
  LENS_FAMILY.COMPOUND_V2,
  LENS_FAMILY.SILO,
  LENS_FAMILY.LLAMALEND,
])

/**
 * Families v6 must NOT be asked for.
 *
 * `AAVE_V4` — v6's last row word is `getUserPosition`'s FIRST field
 * (`drawnShares`), but the converter reads only its LAST one,
 * `dynamicConfigKey`, which selects the risk configuration the position is scored
 * under. Feeding it word 0 gives `undefined` -> 0, silently scoring every V4
 * position against config 0. Fixed in v7 (live on the three chains wired
 * 2026-09-16, not yet redeployed to the v6 chains); on v6 this family takes the
 * per-reserve walk.
 *
 * Costs nothing measurable to withhold: the V4 walk is 284 calls across 12 spokes,
 * which already fits in ONE shard, so the lens saves gas here and zero requests.
 * That is also why a two-phase workaround was rejected — the walk is single-phase,
 * so a follow-up read would have turned 1 request into 2.
 */
const V6_WITHHELD: ReadonlySet<LensFamily> = new Set([LENS_FAMILY.AAVE_V4])

/**
 * What v6 can be trusted to answer completely.
 *
 * The withheld set is the mechanism that makes a mixed rollout survivable: a
 * family whose contract cannot answer completely is simply not asked, and the
 * caller takes the read set it always had. It carried the v3/v5 Aave split, and
 * it carries this until v7 lands.
 */
const ALL_FAMILIES: ReadonlySet<LensFamily> = new Set(
  (Object.values(LENS_FAMILY) as LensFamily[]).filter(
    (f) => !V6_WITHHELD.has(f),
  ),
)

/** Every family — v7 has nothing to withhold. */
const V7_FAMILIES: ReadonlySet<LensFamily> = new Set(
  Object.values(LENS_FAMILY) as LensFamily[],
)

const v6 = (): LensDeployment => ({
  address: POSITION_LENS_V6,
  families: ALL_FAMILIES,
})

const v7 = (): LensDeployment => ({
  address: POSITION_LENS_V7,
  families: V7_FAMILIES,
})

/**
 * Live, runtime-verified deployments.
 *
 * Verified against each chain's public RPC: identical runtime bytecode and a live
 * `getUserData([]) -> []`. Do NOT add a chain on the strength of a deploy script's
 * output alone — the address is deterministic, so a wrong entry is wrong SILENTLY
 * and every read for those families on that chain returns nothing.
 *
 * TWO STALE-NODE LESSONS, both real. XDC's `rpc.xdc.org` answered `0x` for this
 * address at a chain head 54 DAYS OLD — HTTP 200, well-formed JSON, silently
 * frozen — so it reported a deployed contract as absent. Verify against a node
 * whose head is current, and prefer two independent endpoints. Separately,
 * `rpc.sentio.xyz/soneium-mainnet` ACCEPTS a deploy and returns a hash without
 * ever getting it included: it read pending nonce 2 for the deployer while the
 * canonical node still read 0. An endpoint that pools without forwarding looks
 * exactly like a slow chain from the client side.
 *
 * `V1_FAMILIES` is kept for any chain not yet moved to v3 — the family set is what
 * makes a mixed rollout safe, since asking a deployment for a family it does not
 * implement returns empty bytes that read as "no positions".
 */
export const POSITION_LENS: { [chainId: string]: LensDeployment } = {
  [Chain.ETHEREUM_MAINNET]: v6(),
  [Chain.ARBITRUM_ONE]: v6(),
  [Chain.AVALANCHE_C_CHAIN]: v6(),
  [Chain.BNB_SMART_CHAIN_MAINNET]: v6(),
  [Chain.BASE]: v6(),
  [Chain.SONIC_MAINNET]: v6(),
  [Chain.SEI_NETWORK]: v6(),
  [Chain.KATANA]: v6(),
  [Chain.HYPEREVM]: v6(),
  [Chain.MANTLE]: v6(),
  [Chain.XDC_NETWORK]: v6(),
  [Chain.OP_MAINNET]: v6(),
  [Chain.POLYGON_MAINNET]: v6(),
  [Chain.LINEA]: v6(),
  [Chain.SONEIUM]: v6(),
  [Chain.PLASMA_MAINNET]: v6(),
  [Chain.HEMI_NETWORK]: v6(),
  [Chain.SCROLL]: v6(),
  [Chain.FLARE_MAINNET]: v6(),
  [Chain.KAIA_MAINNET]: v6(),
  [Chain.CORE_BLOCKCHAIN_MAINNET]: v6(),
  [Chain.MONAD_MAINNET]: v6(),
  // v7 from day one — deployed 2026-09-16, never ran an older version.
  [Chain.ARC]: v7(),
  [Chain.TEMPO_MAINNET_PRESTO]: v7(),
  [Chain.WORLD_CHAIN]: v7(),
  [Chain.RONIN_MAINNET]: v7(),
}

/**
 * The lens address to use for `family` on `chainId`, or `undefined` — which every
 * caller must read as "use the per-market read set", never as "no positions here".
 *
 * Two distinct reasons for `undefined`, deliberately collapsed because the caller's
 * response is identical: no deployment on this chain, or a deployment that predates
 * this family.
 */
export const getPositionLensFor = (
  chainId: string,
  family: LensFamily,
): string | undefined => {
  const dep = POSITION_LENS[chainId]
  if (!dep || !dep.families.has(family)) return undefined
  return dep.address
}

export const PositionLensAbi = [
  {
    inputs: [
      {
        components: [
          { name: 'family', type: 'uint8' },
          { name: 'payload', type: 'bytes' },
        ],
        name: 'queries',
        type: 'tuple[]',
      },
      { name: 'user', type: 'address' },
    ],
    name: 'getUserData',
    outputs: [{ name: '', type: 'bytes[]' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

const WORD = 32

/** Read `len` bytes at `off` as a big-endian BigInt. */
function readBE(buf: Uint8Array, off: number, len: number): bigint {
  let v = 0n
  for (let i = 0; i < len; i++) v = (v << 8n) | BigInt(buf[off + i])
  return v
}

/** Reinterpret a 32-byte big-endian word as a signed int256. */
export function asInt256(v: bigint): bigint {
  return v >= 1n << 255n ? v - (1n << 256n) : v
}

export function toBytes(hex: string): Uint8Array {
  const h = hex.startsWith('0x') ? hex.slice(2) : hex
  if (h.length === 0) return new Uint8Array(0)
  const out = new Uint8Array(h.length / 2)
  for (let i = 0; i < out.length; i++)
    out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16)
  return out
}

/**
 * One decoded row: its position in the roster the caller sent, the flag byte, and
 * the fixed-width words behind it.
 */
export interface LensRow {
  index: number
  flags: number
  words: bigint[]
}

/**
 * Decode a family blob: `uint16 count` then `count` records of
 * `uint16 index[, uint8 flags], wordCount x uint256`.
 *
 * THE FLAGS BYTE IS PER FAMILY, NOT UNIVERSAL. Compound and LlamaLend rows carry
 * one; Silo rows do NOT — its `_siloSide` output is ten bare words, because there
 * is nothing per-row to flag. Defaulting to 1 and letting Silo pass 0 is deliberate
 * over inferring the stride from the blob length: an inferred stride silently
 * accepts a blob the contract did not emit, and a row read one byte out of phase
 * decodes into plausible-looking balances rather than an error.
 *
 * THROWS on a truncated blob rather than returning the rows it managed to read. A
 * short blob is a decoder/contract disagreement, and half a position set is worse
 * than none: the caller's failure path drops the family and says so, while a
 * silently truncated set renders as "these markets are empty".
 *
 * @param headerBytes extra leading bytes before `count` (Compound's `assetsInOk`).
 */
export function decodeLensRows(
  blob: string,
  wordCount: number,
  headerBytes = 0,
  flagBytes: 0 | 1 = 1,
): { header: Uint8Array; rows: LensRow[] } {
  const buf = toBytes(blob)
  const minimum = headerBytes + 2
  if (buf.length < minimum) {
    throw new Error(
      `position-lens: blob too short (${buf.length} < ${minimum})`,
    )
  }
  const header = buf.slice(0, headerBytes)
  const count = Number(readBE(buf, headerBytes, 2))
  const entrySize = 2 + flagBytes + wordCount * WORD
  const expected = minimum + count * entrySize
  if (buf.length !== expected) {
    throw new Error(
      `position-lens: blob is ${buf.length} bytes, expected ${expected} for ${count} rows of ${wordCount} words`,
    )
  }

  const rows: LensRow[] = []
  let off = minimum
  for (let i = 0; i < count; i++) {
    const index = Number(readBE(buf, off, 2))
    const flags = flagBytes === 1 ? Number(readBE(buf, off + 2, 1)) : 0
    off += 2 + flagBytes
    const words: bigint[] = []
    for (let k = 0; k < wordCount; k++) {
      words.push(readBE(buf, off, WORD))
      off += WORD
    }
    rows.push({ index, flags, words })
  }
  return { header, rows }
}

/** One variable-width row: a fixed head plus a per-row tail. */
export interface VariableLensRow {
  index: number
  /** The fixed words, in emit order. */
  fixed: bigint[]
  /** The variable tail — `tailCount` words, e.g. one balance per collateral. */
  tail: bigint[]
}

/**
 * Decode rows whose width is announced per row rather than fixed by the layout.
 *
 * `decodeLensRows` cannot express these: it derives the stride from a constant
 * word count, and Term and Midnight both emit
 * `uint16 index, uint8 tailCount, <fixedWords>, tailCount x uint256` — a Term repo
 * carries one collateral entry per accepted token, a Midnight market one per
 * collateral index. Forcing the fixed-width helper onto them reads the NEXT row's
 * index as part of the current row's last balance, which produces a plausible
 * number rather than an error.
 *
 * The `tailCount` byte is what makes the walk possible, so it is trusted only as
 * far as the buffer allows: a row claiming more words than remain is a malformed
 * blob, and this throws rather than returning a short read that a caller would
 * treat as a real position.
 */
export function decodeVariableLensRows(
  blob: string,
  fixedWords: number,
  headerBytes = 0,
): { header: Uint8Array; rows: VariableLensRow[] } {
  const buf = toBytes(blob)
  const minimum = headerBytes + 2
  if (buf.length < minimum) {
    throw new Error(
      `position-lens: blob too short (${buf.length} < ${minimum})`,
    )
  }
  const header = buf.slice(0, headerBytes)
  const count = Number(readBE(buf, headerBytes, 2))

  const rows: VariableLensRow[] = []
  let off = minimum
  for (let i = 0; i < count; i++) {
    // index(2) + tailCount(1) must both be present before either is trusted.
    if (off + 3 > buf.length) {
      throw new Error(`position-lens: row ${i} header runs past the blob`)
    }
    const index = Number(readBE(buf, off, 2))
    const tailCount = buf[off + 2]
    off += 3
    const words = fixedWords + tailCount
    if (off + words * WORD > buf.length) {
      throw new Error(
        `position-lens: row ${i} claims ${tailCount} tail words, only ${(buf.length - off) / WORD} remain`,
      )
    }
    const all: bigint[] = []
    for (let k = 0; k < words; k++) {
      all.push(readBE(buf, off, WORD))
      off += WORD
    }
    rows.push({
      index,
      fixed: all.slice(0, fixedWords),
      tail: all.slice(fixedWords),
    })
  }
  // A trailing remainder means the emitter and this decoder disagree — fail loudly.
  if (off !== buf.length) {
    throw new Error(
      `position-lens: ${buf.length - off} trailing bytes after ${count} rows`,
    )
  }
  return { header, rows }
}
